import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import type { CustomerRecord, CustomerUpdateValues, CustomerCreateValues } from '../types';
import { useCustomerTypes } from '../hooks';
import './CustomerFormModal.scss';

const updateSchema = z.object({
  name: z.string().min(1, 'Tên công ty là bắt buộc').max(200, 'Tên công ty tối đa 200 ký tự'),
  representative_name: z.string().max(120, 'Tên người đại diện tối đa 120 ký tự').optional().or(z.literal('')),
  representative_phone: z.string().max(20, 'Số điện thoại tối đa 20 ký tự').optional().or(z.literal('')),
  phone: z.string().max(20, 'Số điện thoại tối đa 20 ký tự').optional().or(z.literal('')),
  address: z.string().max(500, 'Địa chỉ tối đa 500 ký tự').optional().or(z.literal('')),
  tax_code: z.string().max(50, 'Mã số thuế tối đa 50 ký tự').optional().or(z.literal('')),
  logo: z.union([z.string().url('URL logo không hợp lệ'), z.instanceof(File)]).optional().or(z.literal('')),
  status: z.enum(['active', 'inactive']).optional(),
  customer_type_id: z.string().optional().or(z.literal('')),
  source: z.string().max(200, 'Nguồn khách hàng tối đa 200 ký tự').optional().or(z.literal('')),
  notes: z.string().max(1000, 'Ghi chú tối đa 1000 ký tự').optional().or(z.literal('')),
});

const createSchema = z.object({
  name: z.string().min(1, 'Tên công ty là bắt buộc').max(200, 'Tên công ty tối đa 200 ký tự'),
  representative_name: z.string().max(120, 'Tên người đại diện tối đa 120 ký tự').optional().or(z.literal('')),
  representative_phone: z.string().max(20, 'Số điện thoại tối đa 20 ký tự').optional().or(z.literal('')),
  email: z.string().min(1, 'Email là bắt buộc').email('Email không hợp lệ'),
  phone: z.string().max(20, 'Số điện thoại tối đa 20 ký tự').optional().or(z.literal('')),
  address: z.string().max(500, 'Địa chỉ tối đa 500 ký tự').optional().or(z.literal('')),
  tax_code: z.string().max(50, 'Mã số thuế tối đa 50 ký tự').optional().or(z.literal('')),
  logo: z.union([z.string().url('URL logo không hợp lệ'), z.instanceof(File)]).optional().or(z.literal('')),
  status: z.enum(['active', 'inactive']).optional(),
  customer_type_id: z.string().optional().or(z.literal('')),
  source: z.string().max(200, 'Nguồn khách hàng tối đa 200 ký tự').optional().or(z.literal('')),
  notes: z.string().max(1000, 'Ghi chú tối đa 1000 ký tự').optional().or(z.literal('')),
  password: z.string().min(8, 'Mật khẩu phải có ít nhất 8 ký tự'),
  passwordConfirm: z.string().min(8, 'Mật khẩu xác nhận phải có ít nhất 8 ký tự'),
  sendInvite: z.boolean().optional(),
}).refine((data) => data.password === data.passwordConfirm, {
  message: 'Mật khẩu xác nhận không khớp',
  path: ['passwordConfirm'],
});

type Mode = 'create' | 'edit';

interface CustomerFormModalProps {
  open: boolean;
  mode?: Mode;
  onClose: () => void;
  onSubmitCreate?: (payload: CustomerCreateValues) => Promise<void> | void;
  onSubmitUpdate: (id: string, payload: CustomerUpdateValues) => Promise<void> | void;
  initialData?: CustomerRecord | null;
  isSubmitting?: boolean;
}

export function CustomerFormModal({
  open,
  mode = 'edit',
  onClose,
  onSubmitCreate,
  onSubmitUpdate,
  initialData,
  isSubmitting = false,
}: CustomerFormModalProps) {
  const { data: customerTypes = [], isLoading: isLoadingTypes } = useCustomerTypes();

  const isCreateMode = mode === 'create';
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<CustomerCreateValues | CustomerUpdateValues>({
    resolver: zodResolver(mode === 'create' ? createSchema : updateSchema),
    defaultValues: {
      name: '',
      representative_name: '',
      representative_phone: '',
      email: '',
      phone: '',
      address: '',
      tax_code: '',
      logo: '',
      status: 'active',
      customer_type_id: '',
      source: '',
      notes: '',
      password: '',
      passwordConfirm: '',
      sendInvite: true,
    },
  });

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && initialData) {
        reset({
          name: initialData.name,
          representative_name: initialData.representative_name ?? '',
          representative_phone: initialData.representative_phone ?? '',
          phone: initialData.phone ?? '',
          address: initialData.address ?? '',
          tax_code: initialData.tax_code ?? '',
          logo: initialData.logo ?? '',
          status: initialData.status,
          customer_type_id: initialData.customer_type_id ?? '',
          source: initialData.source ?? '',
          notes: initialData.notes ?? '',
        });
        // Set preview if logo exists
        if (initialData.logo) {
          setLogoPreview(initialData.logo);
        } else {
          setLogoPreview(null);
        }
        setLogoFile(null);
      } else if (mode === 'create') {
        reset({
          name: '',
          representative_name: '',
          representative_phone: '',
          email: '',
          phone: '',
          address: '',
          tax_code: '',
          logo: '',
          status: 'active',
          customer_type_id: '',
          source: '',
          notes: '',
          password: '',
          passwordConfirm: '',
          sendInvite: true,
        });
        setLogoPreview(null);
        setLogoFile(null);
      }
    } else {
      reset();
      setLogoPreview(null);
      setLogoFile(null);
    }
  }, [open, mode, initialData, reset]);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [open]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Vui lòng chọn file hình ảnh');
        e.target.value = '';
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Kích thước file không được vượt quá 5MB');
        e.target.value = '';
        return;
      }

      setLogoFile(file);
      setValue('logo', file as File);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const submitHandler = handleSubmit(async (values) => {
    if (mode === 'create' && onSubmitCreate) {
      await onSubmitCreate({
        ...(values as CustomerCreateValues),
        status: values.status ?? 'active',
        logo: logoFile ? logoFile : (values.logo as string | undefined),
      });
    } else if (initialData) {
      await onSubmitUpdate(initialData.id, {
        ...(values as CustomerUpdateValues),
        logo: logoFile ? logoFile : (values.logo as string | undefined),
      });
    }
    onClose();
  });

  if (!open) return null;

  return (
    <div className="hrm-modal-overlay">
      <div className="hrm-modal" role="dialog" aria-modal="true">
        <div className="modal-dialog">
          <div className="hrm-modal-content modal-content customer-form-modal">
            <div className="modal-header customer-form-modal__header">
              <div className="header-stack">
                <span className="header-icon">
                  <i className="ti ti-building-store" />
                </span>
                <div>
                  <h5 className="modal-title mb-1">
                    {mode === 'create' ? 'Thêm mới khách hàng' : 'Chỉnh sửa thông tin khách hàng'}
                  </h5>
                  <p className="header-subtitle mb-0">
                    {mode === 'create'
                      ? 'Tạo khách hàng mới và tài khoản Admin Công Ty.'
                      : 'Cập nhật thông tin công ty và người đại diện.'}
                  </p>
                </div>
              </div>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
            </div>

            <form onSubmit={submitHandler}>
              <div className="modal-body">
                {mode === 'create' && (
                  <div className="form-section">
                    <div className="section-heading">
                      <span className="section-icon">
                        <i className="ti ti-user-shield" />
                      </span>
                      <div>
                        <p className="section-label">Thông tin tài khoản</p>
                        <h6>Đăng nhập và xác thực</h6>
                      </div>
                    </div>

                    <div className="form-grid form-grid--two">
                      <div className="form-field">
                        <label className="form-label required">Email</label>
                        <input
                          type="email"
                          className={`form-control ${isCreateMode && (errors as Record<string, { message?: string }>).email ? 'is-invalid' : ''}`}
                          placeholder="email@example.com"
                          {...(register('email' as keyof CustomerCreateValues) as ReturnType<typeof register>)}
                        />
                        {isCreateMode && (errors as Record<string, { message?: string }>).email && (
                          <span className="text-danger small">{(errors as Record<string, { message?: string }>).email?.message}</span>
                        )}
                      </div>

                      <div className="form-field">
                        <label className="form-label required">Mật khẩu</label>
                        <input
                          type="password"
                          className={`form-control ${isCreateMode && (errors as Record<string, { message?: string }>).password ? 'is-invalid' : ''}`}
                          placeholder="Tối thiểu 8 ký tự"
                          {...(register('password' as keyof CustomerCreateValues) as ReturnType<typeof register>)}
                        />
                        {isCreateMode && (errors as Record<string, { message?: string }>).password && (
                          <span className="text-danger small">{(errors as Record<string, { message?: string }>).password?.message}</span>
                        )}
                      </div>

                      <div className="form-field">
                        <label className="form-label required">Xác nhận mật khẩu</label>
                        <input
                          type="password"
                          className={`form-control ${isCreateMode && (errors as Record<string, { message?: string }>).passwordConfirm ? 'is-invalid' : ''}`}
                          placeholder="Nhập lại mật khẩu"
                          {...(register('passwordConfirm' as keyof CustomerCreateValues) as ReturnType<typeof register>)}
                        />
                        {isCreateMode && (errors as Record<string, { message?: string }>).passwordConfirm && (
                          <span className="text-danger small">{(errors as Record<string, { message?: string }>).passwordConfirm?.message}</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="form-section">
                  <div className="section-heading">
                    <span className="section-icon">
                      <i className="ti ti-building" />
                    </span>
                    <div>
                      <p className="section-label">Thông tin công ty</p>
                      <h6>Tên công ty, người đại diện và liên hệ</h6>
                    </div>
                  </div>

                  <div className="form-grid form-grid--two">
                    <div className="form-field form-field--full">
                      <label className="form-label required">Tên công ty</label>
                      <input
                        type="text"
                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                        placeholder="Tên công ty"
                        {...register('name')}
                      />
                      {errors.name && <span className="text-danger small">{errors.name.message}</span>}
                    </div>

                    <div className="form-field">
                      <label className="form-label">Người đại diện</label>
                      <input
                        type="text"
                        className={`form-control ${errors.representative_name ? 'is-invalid' : ''}`}
                        placeholder="Họ tên người đại diện"
                        {...register('representative_name')}
                      />
                      {errors.representative_name && (
                        <span className="text-danger small">{errors.representative_name.message}</span>
                      )}
                    </div>

                    <div className="form-field">
                      <label className="form-label">SĐT người đại diện</label>
                      <input
                        type="text"
                        className={`form-control ${errors.representative_phone ? 'is-invalid' : ''}`}
                        placeholder="Số điện thoại"
                        {...register('representative_phone')}
                      />
                      {errors.representative_phone && (
                        <span className="text-danger small">{errors.representative_phone.message}</span>
                      )}
                    </div>

                    <div className="form-field">
                      <label className="form-label">SĐT công ty</label>
                      <input
                        type="text"
                        className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                        placeholder="Số điện thoại công ty"
                        {...register('phone')}
                      />
                      {errors.phone && <span className="text-danger small">{errors.phone.message}</span>}
                    </div>

                    <div className="form-field form-field--full">
                      <label className="form-label">Địa chỉ</label>
                      <textarea
                        className={`form-control ${errors.address ? 'is-invalid' : ''}`}
                        rows={3}
                        placeholder="Địa chỉ công ty"
                        {...register('address')}
                      />
                      {errors.address && <span className="text-danger small">{errors.address.message}</span>}
                    </div>

                    <div className="form-field">
                      <label className="form-label">Mã số thuế</label>
                      <input
                        type="text"
                        className={`form-control ${errors.tax_code ? 'is-invalid' : ''}`}
                        placeholder="Mã số thuế"
                        {...register('tax_code')}
                      />
                      {errors.tax_code && <span className="text-danger small">{errors.tax_code.message}</span>}
                    </div>

                    <div className="form-field form-field--full">
                      <label className="form-label">Logo</label>
                      <input
                        type="file"
                        accept="image/*"
                        className={`form-control ${errors.logo ? 'is-invalid' : ''}`}
                        onChange={handleLogoChange}
                      />
                      {errors.logo && <span className="text-danger small">{errors.logo.message}</span>}
                      {logoPreview && (
                        <div style={{ marginTop: '8px' }}>
                          <img
                            src={logoPreview}
                            alt="Logo preview"
                            style={{
                              maxWidth: '200px',
                              maxHeight: '200px',
                              objectFit: 'contain',
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px',
                              padding: '4px',
                              backgroundColor: '#fff',
                            }}
                          />
                        </div>
                      )}
                      <small className="form-text text-muted">
                        Chọn file hình ảnh (JPG, PNG, GIF). Kích thước tối đa 5MB.
                      </small>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="section-heading">
                    <span className="section-icon">
                      <i className="ti ti-info-circle" />
                    </span>
                    <div>
                      <p className="section-label">Thông tin bổ sung</p>
                      <h6>Loại khách hàng, nguồn và ghi chú</h6>
                    </div>
                  </div>

                  <div className="form-grid form-grid--two">
                    <div className="form-field">
                      <label className="form-label">Loại khách hàng</label>
                      <select
                        className={`form-select ${errors.customer_type_id ? 'is-invalid' : ''}`}
                        {...register('customer_type_id')}
                        disabled={isLoadingTypes}
                      >
                        <option value="">-- Chọn loại khách hàng --</option>
                        {customerTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                      {errors.customer_type_id && (
                        <span className="text-danger small">{errors.customer_type_id.message}</span>
                      )}
                    </div>

                    <div className="form-field">
                      <label className="form-label">Nguồn khách hàng</label>
                      <input
                        type="text"
                        className={`form-control ${errors.source ? 'is-invalid' : ''}`}
                        placeholder="Ví dụ: Google Ads, Facebook, Giới thiệu..."
                        {...register('source')}
                      />
                      {errors.source && <span className="text-danger small">{errors.source.message}</span>}
                    </div>

                    <div className="form-field">
                      <label className="form-label">Trạng thái</label>
                      <select className="form-select" {...register('status')}>
                        <option value="active">Đang hoạt động</option>
                        <option value="inactive">Vô hiệu hóa</option>
                      </select>
                    </div>

                    <div className="form-field form-field--full">
                      <label className="form-label">Ghi chú</label>
                      <textarea
                        className={`form-control ${errors.notes ? 'is-invalid' : ''}`}
                        rows={4}
                        placeholder="Ví dụ: Khách hàng khó tính, cần chăm sóc kỹ..."
                        {...register('notes')}
                      />
                      {errors.notes && <span className="text-danger small">{errors.notes.message}</span>}
                    </div>
                  </div>
                </div>

                {mode === 'edit' && initialData?.email && (
                  <div className="form-section">
                    <div className="section-heading">
                      <span className="section-icon">
                        <i className="ti ti-mail" />
                      </span>
                      <div>
                        <p className="section-label">Thông tin tài khoản</p>
                        <h6>Email đăng nhập</h6>
                      </div>
                    </div>

                    <div className="form-grid form-grid--two">
                      <div className="form-field">
                        <label className="form-label">Email</label>
                        <input type="email" className="form-control" value={initialData.email} disabled />
                        <small className="form-text text-muted">Email không thể thay đổi sau khi tạo.</small>
                      </div>
                    </div>
                  </div>
                )}

                {mode === 'create' && (
                  <div className="form-section">
                    <div className="section-heading">
                      <span className="section-icon">
                        <i className="ti ti-mail" />
                      </span>
                      <div>
                        <p className="section-label">Thông báo</p>
                        <h6>Tùy chọn gửi email mời</h6>
                      </div>
                    </div>

                    <div className="form-check">
                      <input
                        id="sendInvite"
                        type="checkbox"
                        className="form-check-input"
                        defaultChecked
                        {...register('sendInvite')}
                      />
                      <label htmlFor="sendInvite" className="form-check-label">
                        Gửi email thông tin đăng nhập cho khách hàng
                      </label>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={isSubmitting}>
                  Hủy
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Đang lưu…' : mode === 'create' ? 'Tạo mới' : 'Cập nhật'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
